import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PROJECT_ROLE_LABELS, normalizeProjectRole, type ProjectRoleCanonical } from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import { LoadingState } from '@/components/StateMessage/StateMessage';
import { useProjectRole } from '@/project/useProjectRole';
import styles from '@/pages/ProjectsPage.module.css';

const ASSIGNABLE_ROLES: ProjectRoleCanonical[] = ['viewer', 'editor', 'sender', 'admin'];

type Props = {
  projectId: string;
};

export function ProjectMembersPanel({ projectId }: Props) {
  const qc = useQueryClient();
  const { can } = useProjectRole(projectId);

  const membersQ = useQuery({
    queryKey: ['project-members', projectId],
    enabled: can('members_manage'),
    queryFn: () =>
      apiFetch<{ members: { user_id: string; email: string; role: string }[] }>(
        `/api/projects/${projectId}/members`,
      ),
  });

  const patchM = useMutation({
    mutationFn: (payload: { userId: string; role: string }) =>
      apiFetch(`/api/projects/${projectId}/members/${payload.userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role: payload.role }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['project-members', projectId] }),
  });

  if (!can('members_manage')) {
    return <p className={styles.mutedSmall}>Alleen project-admins beheren projectrollen.</p>;
  }

  if (membersQ.isLoading) return <LoadingState label="Projectleden laden…" />;

  return (
    <ul className={styles.list}>
      {(membersQ.data?.members ?? []).map((m) => (
        <li key={m.user_id} className={styles.listItem}>
          <div>
            <strong>{m.email}</strong>
            <div className={styles.mutedSmall}>
              {PROJECT_ROLE_LABELS[normalizeProjectRole(m.role)]}
            </div>
          </div>
          <select
            className={styles.filterInput}
            value={normalizeProjectRole(m.role)}
            onChange={(e) =>
              patchM.mutate({ userId: m.user_id, role: e.target.value })
            }
          >
            {ASSIGNABLE_ROLES.map((r) => (
              <option key={r} value={r}>
                {PROJECT_ROLE_LABELS[r]}
              </option>
            ))}
          </select>
        </li>
      ))}
    </ul>
  );
}
